// An exact coarse summary of the collision grid: for each BLOCK x BLOCK square of
// cells, how many are painted (not PLAY_AREA_FREE). A zero means every cell in the
// square is free, so a reader can skip the square's cells entirely.
//
// Bots' path checking (ai/path-checker-ai-handler.js checkTrajectory) reads the
// cells under a candidate head every step; most steps cross empty board, and those
// cell reads were a quarter of all bot time. With this, one or a few block reads
// usually answer "nothing here".
//
// Built lazily, the first time a reader asks for a grid (a one-off full pass), and
// kept exact from then on by the grid's writers: addPlayAreaShape counts every cell
// it paints (painting never frees a cell), and anything that frees cells
// (resetPlayArea, the clear power-up) drops the summary to be rebuilt on next use.
// Kept on the grid array itself (a hidden, non-enumerable property: JSON and clones
// ignore it; a copy just rebuilds), so a grid nobody reads through here costs nothing.
// (Not a WeakMap: core's test runner has none.)
import * as constants from "../constants.js";

export var BLOCK_SHIFT = 3; // 8 x 8 cells per block
var KEY = "_gridBlocks";

function build(playArea) {
    var cols = playArea.cols, rows = playArea.rows, grid = playArea.grid;
    var bcols = (cols >> BLOCK_SHIFT) + 1, brows = (rows >> BLOCK_SHIFT) + 1;
    var counts = new Uint8Array(bcols * brows); // at most 64 per block
    for (var r = 0; r < rows; r++) {
        var base = r * cols, bbase = (r >> BLOCK_SHIFT) * bcols;
        for (var c = 0; c < cols; c++) {
            if (grid[base + c] !== constants.PLAY_AREA_FREE) {counts[bbase + (c >> BLOCK_SHIFT)]++;}
        }
    }
    return {cols: cols, bcols: bcols, counts: counts};
}

// The summary for this play area's grid (built on first use).
export function getGridBlocks(playArea) {
    var b = playArea.grid[KEY];
    if (!b) {
        b = build(playArea);
        Object.defineProperty(playArea.grid, KEY, {value: b, writable: true, configurable: true, enumerable: false});
    }
    return b;
}

// The summary to keep up to date while painting this grid, or null if nobody reads it.
export function trackedGridBlocks(grid) {
    return grid[KEY] || null;
}

// A painted cell that was free (writers call this, with trackedGridBlocks' result).
export function notePainted(b, index) {
    var row = (index / b.cols) | 0;
    b.counts[(row >> BLOCK_SHIFT) * b.bcols + ((index - row * b.cols) >> BLOCK_SHIFT)]++;
}

// Cells were freed: forget the summary (rebuilt on next use).
export function invalidateGridBlocks(grid) {
    if (grid[KEY]) {grid[KEY] = null;}
}
